import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSound } from '@/context/SoundContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bot, Briefcase, User, LogOut, LayoutDashboard, Plus, Menu, X, Crown, Code, ShoppingBag, Brain, Zap, Globe, Store, Link2, Compass, Film, Mail, Download, Smartphone, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import { MemoryManager } from '@/components/MemoryManager';
import { LanguageToggle } from '@/components/LanguageToggle';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { playSound } = useSound();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // PWA Install detection
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else if (isIOS) {
      // Show iOS instructions
      alert('To install: Tap the Share button at the bottom of Safari, then tap "Add to Home Screen"');
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    playSound('whoosh');
    logout();
    navigate('/');
  };

  const handleNavigate = (path) => {
    playSound('click');
    navigate(path);
  };

  const handleLinkClick = () => {
    playSound('pop');
    setMobileMenuOpen(false);
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-teal-500/10 bg-black/70" 
      data-testid="navbar"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group" 
            data-testid="navbar-logo" 
            aria-label="TripleTC Home"
            onClick={() => playSound('pop')}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-green-500 flex items-center justify-center shadow-[0_0_20px_rgba(20,184,166,0.3)] group-hover:shadow-[0_0_30px_rgba(20,184,166,0.5)] transition-shadow" aria-hidden="true">
              <span className="text-white font-black text-sm">TC</span>
            </div>
            <span className="font-black text-xl tracking-tight">
              <span className="text-white">Triple</span>
              <span className="bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">TC</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/agents" 
              className="text-zinc-400 hover:text-teal-400 transition-colors text-sm font-medium"
              data-testid="nav-browse-agents"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              Browse Agents
            </Link>
            <Link 
              to="/my-orders" 
              className="flex items-center gap-1 text-zinc-400 hover:text-teal-400 transition-colors text-sm font-medium"
              data-testid="nav-my-orders"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              <ShoppingBag className="w-4 h-4" aria-hidden="true" />
              My Orders
            </Link>
            <Link 
              to="/templates" 
              className="flex items-center gap-1 text-orange-400 hover:text-orange-300 transition-colors text-sm font-medium"
              data-testid="nav-templates"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              <Zap className="w-4 h-4" aria-hidden="true" />
              Quick Tasks
            </Link>
            <Link 
              to="/pricing" 
              className="text-zinc-400 hover:text-teal-400 transition-colors text-sm font-medium"
              data-testid="nav-pricing"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              Pricing
            </Link>
            <Link 
              to="/showcase" 
              className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-medium"
              data-testid="nav-showcase"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              Showcase
            </Link>
            
            {/* Build Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-medium"
                  onClick={() => playSound('hover')}
                >
                  <Store className="w-4 h-4" aria-hidden="true" />
                  Build
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-800 min-w-[200px]">
                <DropdownMenuItem 
                  onClick={() => { playSound('click'); navigate('/create-agent'); }}
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                >
                  <Bot className="w-4 h-4 mr-2 text-teal-400" />
                  Create Custom Agent
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => { playSound('click'); navigate('/website-builder-pro'); }}
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                >
                  <Globe className="w-4 h-4 mr-2 text-blue-400" />
                  Website Builder
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem 
                  onClick={() => { playSound('click'); navigate('/my-agents'); }}
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                >
                  <Compass className="w-4 h-4 mr-2 text-purple-400" />
                  My Agents
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => { playSound('click'); navigate('/my-websites'); }}
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                >
                  <Link2 className="w-4 h-4 mr-2 text-pink-400" />
                  My Websites
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/subscription" 
              className="flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors text-sm font-medium"
              data-testid="nav-subscription"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              <Crown className="w-4 h-4" aria-hidden="true" />
              Premium
            </Link>
{/* API Link hidden - AI-to-AI not ready for launch */}
            {/* <Link 
              to="/developers" 
              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium"
              data-testid="nav-developers"
              onClick={() => playSound('hover')}
              onMouseEnter={() => playSound('hover')}
            >
              <Code className="w-4 h-4" aria-hidden="true" />
              API
            </Link> */}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <LanguageToggle />
                {user.user_type === 'agent_owner' ? (
                  <Button
                    onClick={() => handleNavigate('/post-agent')}
                    className="bg-transparent border border-teal-500/50 text-teal-400 hover:bg-teal-500/10 rounded-full"
                    data-testid="post-agent-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    List Agent
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleNavigate('/post-job')}
                    className="bg-transparent border border-teal-500/50 text-teal-400 hover:bg-teal-500/10 rounded-full"
                    data-testid="post-job-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Post Job
                  </Button>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-zinc-400 hover:text-white"
                      data-testid="user-menu-trigger"
                      onClick={() => playSound('pop')}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.is_subscriber ? 'bg-green-500/20 border border-green-500/50' : 'bg-zinc-800 border border-zinc-700'}`}>
                        {user.is_subscriber ? (
                          <Crown className="w-4 h-4 text-green-400" />
                        ) : (
                          <User className="w-4 h-4 text-zinc-400" />
                        )}
                      </div>
                      <span className="text-sm">{user.name}</span>
                      {user.is_subscriber && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">PRO</span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/user-dashboard')}
                      className="text-zinc-300 hover:text-white cursor-pointer"
                      data-testid="nav-user-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      My Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/dashboard')}
                      className="text-zinc-300 hover:text-white cursor-pointer"
                      data-testid="nav-dashboard"
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Agent Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/my-orders')}
                      className="text-zinc-300 hover:text-white cursor-pointer"
                      data-testid="nav-my-orders-menu"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/referral')}
                      className="text-teal-400 hover:text-teal-300 cursor-pointer"
                      data-testid="nav-referral"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Refer a Friend
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { playSound('pop'); setMemoryOpen(true); }}
                      className="text-purple-400 hover:text-purple-300 cursor-pointer"
                      data-testid="nav-memory"
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      Memory & Projects
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/my-chatbots')}
                      className="text-blue-400 hover:text-blue-300 cursor-pointer"
                      data-testid="nav-my-chatbots"
                    >
                      <Bot className="w-4 h-4 mr-2" />
                      My Chatbots
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/video-gallery')}
                      className="text-pink-400 hover:text-pink-300 cursor-pointer"
                      data-testid="nav-video-gallery"
                    >
                      <Film className="w-4 h-4 mr-2" />
                      Video Gallery
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    {/* Owner Admin - Only visible to owner */}
                    {(user?.email === 'rossapallooza@gmail.com' || user?.is_owner) && (
                      <DropdownMenuItem 
                        onClick={() => handleNavigate('/owner-admin')}
                        className="text-amber-400 hover:text-amber-300 cursor-pointer"
                        data-testid="nav-owner-admin"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Owner Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/agent-memory')}
                      className="text-purple-400 hover:text-purple-300 cursor-pointer"
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      AI Memory
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/browser-agent')}
                      className="text-teal-400 hover:text-teal-300 cursor-pointer"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Browser Agent
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/marketplace')}
                      className="text-green-400 hover:text-green-300 cursor-pointer"
                    >
                      <Store className="w-4 h-4 mr-2" />
                      Marketplace
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/integrations')}
                      className="text-blue-400 hover:text-blue-300 cursor-pointer"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Connect Apps
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/architect')}
                      className="text-yellow-400 hover:text-yellow-300 cursor-pointer"
                    >
                      <Compass className="w-4 h-4 mr-2" />
                      Product Architect
                    </DropdownMenuItem>
                    {!user.is_subscriber && (
                      <DropdownMenuItem 
                        onClick={() => handleNavigate('/subscription')}
                        className="text-green-400 hover:text-green-300 cursor-pointer"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade to Premium
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem 
                      onClick={() => handleNavigate('/contact')}
                      className="text-zinc-400 hover:text-zinc-300 cursor-pointer"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Support
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-400 hover:text-red-300 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <LanguageToggle />
                <Button
                  variant="ghost"
                  onClick={() => handleNavigate('/auth')}
                  className="text-zinc-400 hover:text-white"
                  data-testid="login-btn"
                  aria-label="Log in to your account"
                >
                  Log In
                </Button>
                <Button
                  onClick={() => handleNavigate('/auth?mode=register')}
                  className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-400 hover:to-green-400 text-white font-semibold rounded-full shadow-[0_0_20px_rgba(20,184,166,0.3)] hover:shadow-[0_0_30px_rgba(20,184,166,0.5)] transition-all"
                  data-testid="signup-btn"
                  aria-label="Create a new account"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => {
              playSound('pop');
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            data-testid="mobile-menu-btn"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          id="mobile-menu"
          className="md:hidden bg-zinc-900/95 backdrop-blur-xl border-t border-amber-500/10 px-6 py-4"
          role="menu"
        >
          <div className="flex flex-col gap-4">
            <Link 
              to="/agents" 
              className="text-zinc-300 hover:text-teal-400 py-2"
              onClick={handleLinkClick}
            >
              Browse Agents
            </Link>
            <Link 
              to="/my-orders" 
              className="text-zinc-300 hover:text-teal-400 py-2 flex items-center gap-2"
              onClick={handleLinkClick}
            >
              <ShoppingBag className="w-4 h-4" />
              My Orders
            </Link>
            <Link 
              to="/subscription" 
              className="text-green-400 hover:text-green-300 py-2 flex items-center gap-2"
              onClick={handleLinkClick}
            >
              <Crown className="w-4 h-4" />
              Premium
            </Link>
            <Link 
              to="/templates" 
              className="text-orange-400 hover:text-orange-300 py-2 flex items-center gap-2"
              onClick={handleLinkClick}
            >
              <Zap className="w-4 h-4" />
              Quick Tasks
            </Link>
            <Link 
              to="/pricing" 
              className="text-zinc-300 hover:text-teal-400 py-2"
              onClick={handleLinkClick}
            >
              Pricing
            </Link>
            
            {/* Build Section in Mobile */}
            <div className="border-t border-zinc-800 pt-4 mt-2">
              <span className="text-zinc-500 text-xs uppercase tracking-wider">Build</span>
              <Link 
                to="/create-agent" 
                className="text-cyan-400 hover:text-cyan-300 py-2 flex items-center gap-2 mt-2"
                onClick={handleLinkClick}
              >
                <Bot className="w-4 h-4" />
                Create Custom Agent
              </Link>
              <Link 
                to="/website-builder-pro" 
                className="text-blue-400 hover:text-blue-300 py-2 flex items-center gap-2"
                onClick={handleLinkClick}
              >
                <Globe className="w-4 h-4" />
                Website Builder
              </Link>
              <Link 
                to="/my-agents" 
                className="text-zinc-300 hover:text-teal-400 py-2 flex items-center gap-2"
                onClick={handleLinkClick}
              >
                <Compass className="w-4 h-4" />
                My Agents
              </Link>
              <Link 
                to="/my-websites" 
                className="text-zinc-300 hover:text-teal-400 py-2 flex items-center gap-2"
                onClick={handleLinkClick}
              >
                <Link2 className="w-4 h-4" />
                My Websites
              </Link>
            </div>
            
            {user ? (
              <>
                <Link 
                  to="/user-dashboard" 
                  className="text-zinc-300 hover:text-white py-2 flex items-center gap-2"
                  onClick={handleLinkClick}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  My Dashboard
                </Link>
                <Link 
                  to="/referral" 
                  className="text-teal-400 hover:text-teal-300 py-2 flex items-center gap-2"
                  onClick={handleLinkClick}
                >
                  <User className="w-4 h-4" />
                  Refer a Friend
                </Link>
                <Link 
                  to="/dashboard" 
                  className="text-zinc-300 hover:text-white py-2"
                  onClick={handleLinkClick}
                >
                  Agent Dashboard
                </Link>
                <Link 
                  to="/my-chatbots" 
                  className="text-blue-400 hover:text-blue-300 py-2 flex items-center gap-2"
                  onClick={handleLinkClick}
                >
                  <Bot className="w-4 h-4" />
                  My Chatbots
                </Link>
                <button 
                  onClick={() => { playSound('pop'); setMemoryOpen(true); setMobileMenuOpen(false); }}
                  className="text-teal-400 hover:text-teal-300 py-2 text-left flex items-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Memory & Projects
                </button>
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="text-red-400 hover:text-red-300 py-2 text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link 
                to="/auth" 
                className="text-teal-400 hover:text-teal-300 py-2"
                onClick={handleLinkClick}
              >
                Log In / Sign Up
              </Link>
            )}
            
            {/* Install App Button - Show if not already installed */}
            {!isStandalone && (
              <button 
                onClick={handleInstallClick}
                className="mt-2 pt-4 border-t border-zinc-700 text-cyan-400 hover:text-cyan-300 py-2 text-left flex items-center gap-2 w-full"
              >
                <Smartphone className="w-4 h-4" />
                {isIOS ? 'Add to Home Screen' : (deferredPrompt ? 'Install App' : 'Add to Home Screen')}
              </button>
            )}
            
            {/* Language Toggle in Mobile Menu */}
            <div className="mt-2 pt-4 border-t border-zinc-700 flex items-center gap-2">
              <Globe className="w-4 h-4 text-zinc-500" />
              <span className="text-zinc-500 text-sm">Language:</span>
              <LanguageToggle />
            </div>
          </div>
        </div>
      )}
      
      {/* Memory Manager Modal */}
      <MemoryManager isOpen={memoryOpen} onClose={() => setMemoryOpen(false)} />
    </nav>
  );
};

export default Navbar;
